import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, Spin, message, Input, Badge, Empty, Select, Checkbox, Collapse } from 'antd';
import {
  ClockCircleOutlined, CheckOutlined, CloseOutlined,
  SaveOutlined, CaretDownOutlined, CaretUpOutlined,
  PlusOutlined, DeleteOutlined, MessageOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getSlotDetail, saveSlotNote, saveAttendances } from '../../api/schedule';
import { getStudentNotes, addStudentNote, deleteStudentNote } from '../../api/students';
import api from '../../api/axios';

const { TextArea } = Input;

const getLessonStatus = (startTime, endTime) => {
  const now   = dayjs();
  const start = dayjs(`${dayjs().format('YYYY-MM-DD')} ${startTime}`);
  const end   = dayjs(`${dayjs().format('YYYY-MM-DD')} ${endTime}`);
  if (now.isBefore(start)) return 'upcoming';
  if (now.isAfter(end))    return 'past';
  return 'current';
};

// --- Öğrenci not paneli (mobil) ---
const StudentNotesMini = ({ studentId, studentName }) => {
  const [open, setOpen]     = useState(false);
  const [notes, setNotes]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding]   = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await getStudentNotes(studentId);
      setNotes(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetch(); }, [open, studentId]);

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    try {
      const res = await addStudentNote(studentId, { note: newNote.trim() });
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
    } catch { message.error('Not eklenemedi.'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteStudentNote(studentId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { message.error('Silinemedi.'); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#6b7280', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, padding: 0,
        }}
      >
        <MessageOutlined />
        {open ? 'Notları gizle' : `${studentName} notları`}
        {notes.length > 0 && !open && (
          <span style={{ background: '#e5e7eb', borderRadius: 10, padding: '0 6px', fontSize: 11 }}>{notes.length}</span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 8, padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <div className="flex gap-2 mb-2">
            <Input
              size="small"
              placeholder="Not ekle..."
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              onPressEnter={handleAdd}
            />
            <Button size="small" type="primary" icon={<PlusOutlined />} loading={adding} onClick={handleAdd}>Ekle</Button>
          </div>
          {loading ? <Spin size="small" /> : notes.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9ca3af' }}>Henüz not yok.</div>
          ) : notes.map(n => (
            <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 13 }}>{n.note}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{n.user?.name} · {dayjs(n.created_at).format('DD.MM HH:mm')}</div>
              </div>
              <Button
                type="text" danger size="small" icon={<DeleteOutlined />}
                onClick={() => handleDelete(n.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Tek öğrenci satırı ---
const StudentRow = ({ s, attStatus, onToggle, topicId, onTopicChange, topicCompleted, onTopicCompleted }) => {
  const att = attStatus;
  const hasPresentBg      = att === 'present';
  const hasAbsentBg       = att === 'absent';
  const hasPaidAbsentBg   = att === 'planned_absent_paid';

  const rowBorder = hasPresentBg ? '#b7eb8f' : hasAbsentBg ? '#ffa39e' : hasPaidAbsentBg ? '#ffadd2' : '#e5e7eb';
  const rowBg     = hasPresentBg ? '#f6ffed' : hasAbsentBg ? '#fff2f0' : hasPaidAbsentBg ? '#fff0f6' : '#fafafa';

  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${rowBorder}`,
      background: rowBg,
      padding: '12px 14px',
      marginBottom: 10,
    }}>
      {/* Üst satır: isim + butonlar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{s.student_name}</div>
          <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
            {s.enrollment_type && (
              <Tag color={s.enrollment_type === 'trial' ? 'gold' : s.enrollment_type === 'makeup' ? 'orange' : 'blue'} style={{ fontSize: 10, margin: 0 }}>
                {s.enrollment_type === 'trial' ? 'Deneme' : s.enrollment_type === 'makeup' ? 'Telafi' : 'Normal'}
              </Tag>
            )}
            {s.course_name && <Tag style={{ fontSize: 10, margin: 0 }}>{s.course_name}</Tag>}
          </div>
        </div>

        {/* Geldi / Gelmedi / Gelmeyecek */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => onToggle(s.session_id, 'present')}
            style={{
              width: 48, height: 48, borderRadius: 10, border: '2px solid',
              borderColor: hasPresentBg ? '#52c41a' : '#d9d9d9',
              background: hasPresentBg ? '#52c41a' : '#fff',
              color: hasPresentBg ? '#fff' : '#9ca3af',
              fontSize: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            <CheckOutlined />
          </button>
          <button
            onClick={() => onToggle(s.session_id, 'absent')}
            style={{
              width: 48, height: 48, borderRadius: 10, border: '2px solid',
              borderColor: hasAbsentBg ? '#ff4d4f' : '#d9d9d9',
              background: hasAbsentBg ? '#ff4d4f' : '#fff',
              color: hasAbsentBg ? '#fff' : '#9ca3af',
              fontSize: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            <CloseOutlined />
          </button>
          <button
            onClick={() => onToggle(s.session_id, 'planned_absent_paid')}
            title="Gelmeyecek"
            style={{
              width: 48, height: 48, borderRadius: 10, border: '2px solid',
              borderColor: hasPaidAbsentBg ? '#eb2f96' : '#d9d9d9',
              background: hasPaidAbsentBg ? '#eb2f96' : '#fff',
              color: hasPaidAbsentBg ? '#fff' : '#9ca3af',
              fontSize: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            <CalendarOutlined />
          </button>
        </div>
      </div>

      {/* Proje seçimi */}
      {s.topics && s.topics.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            style={{ flex: 1 }}
            placeholder="Proje seç"
            value={topicId}
            onChange={onTopicChange}
            allowClear
            size="middle"
            options={s.topics.map(t => ({ value: t.id, label: t.name, disabled: t.completed }))}
            optionRender={(opt) => (
              <div className="flex justify-between items-center">
                <span style={{ textDecoration: opt.data.disabled ? 'line-through' : undefined, color: opt.data.disabled ? '#9ca3af' : undefined }}>
                  {opt.label}
                </span>
                {opt.data.disabled && <Tag color="green" style={{ fontSize: 10, margin: 0 }}>✓</Tag>}
              </div>
            )}
          />
          {topicId && (
            <Checkbox checked={topicCompleted} onChange={e => onTopicCompleted(e.target.checked)}>
              <span style={{ fontSize: 13 }}>Tamamlandı</span>
            </Checkbox>
          )}
        </div>
      )}

      {/* Öğrenci notları */}
      <StudentNotesMini studentId={s.student_id} studentName={s.student_name} />
    </div>
  );
};

// --- Tek ders kartı ---
const LessonCard = ({ slot, date }) => {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [students, setStudents] = useState([]);
  const [slotNote, setSlotNote] = useState('');
  const [attendance, setAttendance]       = useState({});
  const [topicIds, setTopicIds]           = useState({});
  const [topicCompleted, setTopicCompleted] = useState({});

  const status = getLessonStatus(slot.start_time, slot.end_time);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getSlotDetail({ date, classroom_id: slot.classroom_id, start_time: (slot.start_time || '').slice(0, 5) });
      const data = res.data;
      setSlotNote(data.note || '');
      setStudents(data.students || []);
      const att = {}, tids = {}, tcomp = {};
      (data.students || []).forEach(s => {
        if (s.attendance?.status)   att[s.session_id]   = s.attendance.status;
        if (s.attendance?.topic_id) tids[s.session_id]  = s.attendance.topic_id;
        tcomp[s.session_id] = s.attendance?.topic_completed || false;
      });
      setAttendance(att);
      setTopicIds(tids);
      setTopicCompleted(tcomp);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Bilinmeyen hata';
      message.error(`Ders detayı yüklenemedi: ${msg}`);
    }
    finally { setLoading(false); }
  }, [date, slot.classroom_id, slot.start_time]);

  useEffect(() => { fetchDetail(); }, []);

  const toggleAttendance = (sessionId, val) =>
    setAttendance(prev => ({ ...prev, [sessionId]: prev[sessionId] === val ? null : val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = students.filter(s => attendance[s.session_id]);
      if (toSave.length > 0) {
        await Promise.all(toSave.map(s =>
          saveAttendances(s.session_id, {
            attendances: [{
              student_id:      s.student_id,
              status:          attendance[s.session_id],
              topic_id:        topicIds[s.session_id]    || null,
              topic_completed: topicCompleted[s.session_id] || false,
            }],
          })
        ));
      }
      await saveSlotNote({ date, classroom_id: slot.classroom_id, start_time: (slot.start_time || '').slice(0, 5), note: slotNote || null });
      message.success('Kaydedildi.');
      fetchDetail();
    } catch { message.error('Kaydedilemedi.'); }
    finally { setSaving(false); }
  };

  const savedCount = students.length > 0
    ? students.filter(s => s.attendance?.status).length
    : (slot.attendance_count || 0);
  const borderColor = status === 'current' ? '#1677ff' : status === 'past' ? '#d9d9d9' : '#8c8c8c';
  const bgColor     = status === 'current' ? '#f0f7ff'  : status === 'past' ? '#fafafa'  : '#fff';

  return (
    <div style={{
      border: `2px solid ${borderColor}`,
      borderRadius: 12,
      background: bgColor,
      marginBottom: 12,
      overflow: 'hidden',
      boxShadow: status === 'current' ? '0 2px 12px #1677ff22' : undefined,
    }}>
      {/* Başlık satırı */}
      <div
        style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'center', minWidth: 48 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: borderColor, lineHeight: 1.1 }}>{slot.start_time}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{slot.end_time}</div>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1d3557' }}>{slot.classroom_name}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {status === 'current' && <Tag color="blue" style={{ margin: 0 }}>Şu an</Tag>}
              {status === 'upcoming' && <Tag color="default" style={{ margin: 0 }}><ClockCircleOutlined className="mr-1" />Yaklaşıyor</Tag>}
              {status === 'past' && savedCount > 0 && <Tag color="green" style={{ margin: 0 }}><CheckOutlined className="mr-1" />Yoklama alındı</Tag>}
              {status === 'past' && savedCount === 0 && slot.total > 0 && <Tag color="red" style={{ margin: 0 }}>Yoklama alınmadı</Tag>}
              <span style={{ fontSize: 12, color: '#6b7280' }}>{slot.total}/{slot.capacity} öğrenci</span>
              {slot.type_counts?.normal  > 0 && <Tag color="blue"   style={{ margin: 0, fontSize: 11, padding: '0 4px' }}>{slot.type_counts.normal} Normal</Tag>}
              {slot.type_counts?.trial   > 0 && <Tag color="gold"   style={{ margin: 0, fontSize: 11, padding: '0 4px' }}>{slot.type_counts.trial} Deneme</Tag>}
              {slot.type_counts?.makeup  > 0 && <Tag color="orange" style={{ margin: 0, fontSize: 11, padding: '0 4px' }}>{slot.type_counts.makeup} Telafi</Tag>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {savedCount > 0 && <span style={{ fontSize: 13, color: '#52c41a', fontWeight: 700 }}>{savedCount}/{slot.total} ✓</span>}
          {open ? <CaretUpOutlined style={{ color: '#6b7280' }} /> : <CaretDownOutlined style={{ color: '#6b7280' }} />}
        </div>
      </div>

      {/* Açılır içerik */}
      {open && (
        <div style={{ borderTop: `1px solid ${borderColor}44`, padding: '14px 16px', background: '#fff' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spin /></div>
          ) : students.length === 0 ? (
            <Empty description="Öğrenci yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {students.map(s => (
                <StudentRow
                  key={s.session_id}
                  s={s}
                  attStatus={attendance[s.session_id]}
                  onToggle={toggleAttendance}
                  topicId={topicIds[s.session_id]}
                  onTopicChange={val => setTopicIds(prev => ({ ...prev, [s.session_id]: val }))}
                  topicCompleted={topicCompleted[s.session_id] || false}
                  onTopicCompleted={val => setTopicCompleted(prev => ({ ...prev, [s.session_id]: val }))}
                />
              ))}

              {/* Ders Notu */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Ders Notu <span style={{ fontWeight: 400, color: '#9ca3af' }}>(tüm öğrenciler için genel not)</span>
                </div>
                <TextArea
                  value={slotNote}
                  onChange={e => setSlotNote(e.target.value)}
                  placeholder="Bu derse ait genel not..."
                  rows={2}
                  style={{ fontSize: 13 }}
                />
              </div>

              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} block size="large">
                Kaydet
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --- Ana panel ---
const TodayLessonsPanel = ({ slots = [], date }) => {
  if (slots.length === 0) {
    return (
      <Card title="Bugünkü Derslerim" size="small">
        <Empty description="Bugün ders yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  const sorted = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const currentCount  = sorted.filter(s => getLessonStatus(s.start_time, s.end_time) === 'current').length;
  const upcomingCount = sorted.filter(s => getLessonStatus(s.start_time, s.end_time) === 'upcoming').length;

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Bugünkü Derslerim</span>
          {currentCount > 0 && <Badge count="Şu an ders var" color="#1677ff" />}
          {currentCount === 0 && upcomingCount > 0 && <Badge count={`${upcomingCount} ders bekliyor`} color="#8c8c8c" />}
        </div>
      }
      size="small"
    >
      {sorted.map((slot, i) => (
        <LessonCard key={i} slot={slot} date={date} />
      ))}
    </Card>
  );
};

export default TodayLessonsPanel;
